/**
 * @file 全ページ共通レイアウト。Head/Header/Footer/Navigation/BreadCrumbs等を組み立て、
 *        ログイン状態で表示構造を切り替える。historyContextで遷移履歴を子コンポーネントに提供。
 */
import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactNode, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { checkIsLogin } from '@/utils/common';
import { Constants } from '@/utils/constants';
import snccStyle from '@/utils/css/snccStyle';
import url from '@/utils/config';

import { providerInfoState } from '@/states/providerInfoState';
import useGetProviderConfig from '@/hooks/providerConfig';
import historyContext from '@/hooks/historyContext';

import BreadCrumbs from '@/components/templates/BreadCrumbs';
import Header from '@/components/templates/Header';
import UserLogout from '@/components/templates/UserLogout';
import Navigation from '@/components/templates/Navigation';
import Footer from '@/components/templates/Footer';
import LoadingIcon from '@/components/templates/LoadingIcon';
import AlertDialog from '@/components/organisms/AlertDialog';

type Props = {
  children: ReactNode;
};

const Layout = ({ children }: Props) => {
  const router = useRouter();
  const [history, setHistory] = useState([router.asPath, '']);
  const [isLogin, setIsLogin] = useState(false);
  const providerInfo = useRecoilValue(providerInfoState);
  const { execute: fetchProviderInfo } = useGetProviderConfig();

  useEffect(() => {
    // プロバイダーIDから事業者情報（ロゴ・ページタイトル等）を動的に取得
    fetchProviderInfo(router.pathname);

    // ログイン/非ログインでUI構造を切替（ナビ・パンくず・ログアウトボタンの表示/非表示）
    if (checkIsLogin(router.pathname)) {
      setIsLogin(true);
    } else {
      setIsLogin(false);
    }

    // 前画面パスを保持して「戻る」制御に利用するため、遷移毎に履歴を更新
    setHistory([router.asPath, history[0]]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchProviderInfo, providerInfo.providerId, router.asPath, router.pathname]);

  return (
    <>
      <Head>
        <meta charSet='UTF-8' />
        <meta
          name='viewport'
          content='width=device-width, user-scalable=yes, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0'
        />
        <title>{providerInfo.pageTitle}</title>
        <meta name='description' content={providerInfo.metaDiscription} />
        <meta name='keywords' content={providerInfo.metaKeywords} />
        <link rel='apple-touch-icon' href={url('/images/apple-touch-icon-precomposed.png')} />
        {/* SNCCの場合のみ専用faviconを適用（FVNO事業者はデフォルトfaviconを使用） */}
        {providerInfo.providerId === Constants.ProviderId.SNCC && (
          <link rel='shortcut icon' href={url('/favicon.ico')} type='image/vnd.microsoft.icon' />
        )}
      </Head>
      {/* SNCC専用のグローバルスタイルを動的に注入（ブランドカラー等の上書き） */}
      {providerInfo.providerId === Constants.ProviderId.SNCC && (
        <style jsx global>
          {snccStyle}
        </style>
      )}
      <div id='top' className={isLogin ? 'contents' : 'login'}>
        <div className='wrapper'>
          <Header />
          <div className='wrapperInner'>
            {isLogin && <UserLogout />}
            <div className='mainContents'>
              <div className='inner'>
                {!isLogin ? (
                  <h2>「{providerInfo.serviceName}」マイページ</h2>
                ) : (
                  <>
                    <Navigation />
                    <BreadCrumbs />
                  </>
                )}

                {/* historyContextで[currentPath, prevPath]を子コンポーネントに提供し、「戻る」ボタンの遷移先決定に使用 */}
                <div className='box'>
                  <historyContext.Provider value={history}>{children}</historyContext.Provider>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <LoadingIcon />
      <AlertDialog />
      <Footer />
    </>
  );
};

export default Layout;
